import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface StepConfig {
    id: string;
    title: string;
    description: string;
}

const OnboardingPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const role = searchParams.get('role') || 'customer';
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<any>({
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        // Driver specific
        vehicleType: '',
        vehiclePlate: '',
        licenseNumber: '',
        // Company specific
        companyName: '',
        registrationNumber: '',
        // Agent specific
        territory: '',
        experience: ''
    });

    const getStepsForRole = (role: string): StepConfig[] => {
        const baseSteps = [
            { id: 'contact', title: 'Contact Info', description: 'Your contact details' },
            { id: 'location', title: 'Location', description: 'Where you operate' }
        ];

        const roleSteps: { [key: string]: StepConfig[] } = {
            customer: [
                ...baseSteps,
                { id: 'preferences', title: 'Preferences', description: 'Delivery preferences' }
            ],
            driver: [
                ...baseSteps,
                { id: 'vehicle', title: 'Vehicle Details', description: 'Your vehicle information' },
                { id: 'documents', title: 'Documents', description: 'Upload required documents' }
            ],
            company: [
                { id: 'company', title: 'Company Info', description: 'Business details' },
                ...baseSteps,
                { id: 'billing', title: 'Billing', description: 'Payment information' }
            ],
            agent: [
                ...baseSteps,
                { id: 'territory', title: 'Territory', description: 'Your coverage area' },
                { id: 'experience', title: 'Experience', description: 'Background information' }
            ]
        };

        return roleSteps[role] || roleSteps.customer;
    };

    const steps = getStepsForRole(role);

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            // Complete onboarding
            console.log('Onboarding complete:', { role: role.toUpperCase(), ...formData });
            navigate('/');
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const renderStepContent = () => {
        const stepId = steps[currentStep]?.id;

        switch (stepId) {
            case 'contact':
                return (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Phone Number</label>
                            <input
                                type="tel"
                                placeholder="+27 XX XXX XXXX"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                    </div>
                );

            case 'location':
                return (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Street Address</label>
                            <input
                                type="text"
                                placeholder="123 Main Street"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>City</label>
                                <input
                                    type="text"
                                    placeholder="Johannesburg"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                    style={inputStyle}
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>Postal Code</label>
                                <input
                                    type="text"
                                    placeholder="2000"
                                    value={formData.postalCode}
                                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                                    style={inputStyle}
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'vehicle':
                return (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Vehicle Type</label>
                            <select
                                value={formData.vehicleType}
                                onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                                style={inputStyle}
                            >
                                <option value="">Select vehicle type</option>
                                <option value="bakkie">Bakkie (Light Truck)</option>
                                <option value="panel-van">Panel Van</option>
                                <option value="truck">Truck (3-8 tons)</option>
                                <option value="large-truck">Large Truck (8+ tons)</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Vehicle License Plate</label>
                            <input
                                type="text"
                                placeholder="ABC 123 GP"
                                value={formData.vehiclePlate}
                                onChange={(e) => setFormData({ ...formData, vehiclePlate: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Driver's License Number</label>
                            <input
                                type="text"
                                placeholder="Enter license number"
                                value={formData.licenseNumber}
                                onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                    </div>
                );

            case 'company':
                return (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Company Name</label>
                            <input
                                type="text"
                                placeholder="Your Company (Pty) Ltd"
                                value={formData.companyName}
                                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Registration Number</label>
                            <input
                                type="text"
                                placeholder="2023/123456/07"
                                value={formData.registrationNumber}
                                onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                    </div>
                );

            case 'preferences':
                return (
                    <div>
                        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
                            Set your delivery preferences to help us serve you better.
                        </p>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}>
                                <input type="checkbox" />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '14px' }}>SMS notifications</div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Get delivery updates via SMS</div>
                                </div>
                            </label>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}>
                                <input type="checkbox" defaultChecked />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '14px' }}>Email notifications</div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Receive booking confirmations</div>
                                </div>
                            </label>
                        </div>
                    </div>
                );

            case 'documents':
                return (
                    <div>
                        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
                            Upload the required documents to verify your driver profile.
                        </p>
                        <div style={uploadBoxStyle}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📄</div>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Driver's License</div>
                            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>Upload a clear copy</div>
                            <input type="file" id="license" style={{ display: 'none' }} />
                            <label htmlFor="license" style={uploadButtonStyle}>Choose File</label>
                        </div>
                        <div style={uploadBoxStyle}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🚗</div>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Vehicle Registration</div>
                            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>Upload vehicle disc</div>
                            <input type="file" id="registration" style={{ display: 'none' }} />
                            <label htmlFor="registration" style={uploadButtonStyle}>Choose File</label>
                        </div>
                    </div>
                );

            case 'billing':
                return (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Billing Email</label>
                            <input
                                type="email"
                                placeholder="billing@company.com"
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>VAT Number (Optional)</label>
                            <input
                                type="text"
                                placeholder="4123456789"
                                style={inputStyle}
                            />
                        </div>
                    </div>
                );

            case 'territory':
                return (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Primary Territory</label>
                            <select style={inputStyle} value={formData.territory} onChange={(e) => setFormData({ ...formData, territory: e.target.value })}>
                                <option value="">Select your territory</option>
                                <option value="johannesburg">Johannesburg & Surrounds</option>
                                <option value="cape-town">Cape Town & Surrounds</option>
                                <option value="durban">Durban & Surrounds</option>
                                <option value="pretoria">Pretoria & Surrounds</option>
                            </select>
                        </div>
                    </div>
                );

            case 'experience':
                return (
                    <div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Years of Experience</label>
                            <select style={inputStyle} value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: e.target.value })}>
                                <option value="">Select experience level</option>
                                <option value="0-1">Less than 1 year</option>
                                <option value="1-3">1-3 years</option>
                                <option value="3-5">3-5 years</option>
                                <option value="5+">5+ years</option>
                            </select>
                        </div>
                    </div>
                );

            default:
                return <div>Unknown step</div>;
        }
    };

    const labelStyle: React.CSSProperties = {
        display: 'block',
        fontSize: '14px',
        fontWeight: 600,
        marginBottom: '8px',
        color: '#374151'
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '12px 16px',
        fontSize: '16px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.2s ease',
        fontFamily: 'inherit'
    };

    const uploadBoxStyle: React.CSSProperties = {
        border: '2px dashed #d1d5db',
        borderRadius: '12px',
        padding: '32px',
        textAlign: 'center',
        marginBottom: '16px',
        backgroundColor: '#f9fafb'
    };

    const uploadButtonStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '8px 24px',
        backgroundColor: '#0066ff',
        color: '#ffffff',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease'
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: '48px 24px'
        }}>
            <div style={{
                maxWidth: '800px',
                margin: '0 auto'
            }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                    <h1 style={{
                        fontSize: '32px',
                        fontWeight: 800,
                        marginBottom: '8px',
                        color: '#1a1a1a'
                    }}>
                        Complete your {role.charAt(0).toUpperCase() + role.slice(1)} profile
                    </h1>
                    <p style={{ fontSize: '16px', color: '#6b7280' }}>
                        Just a few more steps to get started
                    </p>
                </div>

                {/* Progress Stepper */}
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    padding: '32px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                    marginBottom: '24px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '48px',
                        position: 'relative'
                    }}>
                        {/* Progress line */}
                        <div style={{
                            position: 'absolute',
                            top: '20px',
                            left: '32px',
                            right: '32px',
                            height: '2px',
                            backgroundColor: '#e5e7eb',
                            zIndex: 0
                        }}>
                            <div style={{
                                height: '100%',
                                backgroundColor: '#0066ff',
                                width: `${(currentStep / (steps.length - 1)) * 100}%`,
                                transition: 'width 0.3s ease'
                            }}></div>
                        </div>

                        {steps.map((step, index) => (
                            <div key={step.id} style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                position: 'relative',
                                zIndex: 1
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: index <= currentStep ? '#0066ff' : '#e5e7eb',
                                    color: index <= currentStep ? '#ffffff' : '#9ca3af',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    marginBottom: '12px',
                                    transition: 'all 0.3s ease',
                                    border: '3px solid #ffffff'
                                }}>
                                    {index < currentStep ? '✓' : index + 1}
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: index <= currentStep ? '#1a1a1a' : '#9ca3af',
                                    textAlign: 'center'
                                }}>
                                    {step.title}
                                </div>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#9ca3af',
                                    textAlign: 'center',
                                    marginTop: '4px'
                                }}>
                                    {step.description}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Step Content */}
                    <div style={{ minHeight: '300px' }}>
                        {renderStepContent()}
                    </div>

                    {/* Navigation Buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '48px',
                        paddingTop: '24px',
                        borderTop: '1px solid #e5e7eb'
                    }}>
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 0}
                            style={{
                                padding: '12px 32px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 600,
                                border: '2px solid #e5e7eb',
                                backgroundColor: '#ffffff',
                                color: '#6b7280',
                                cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                                opacity: currentStep === 0 ? 0.5 : 1,
                                transition: 'all 0.2s ease'
                            }}
                        >
                            ← Back
                        </button>
                        <button
                            onClick={handleNext}
                            style={{
                                padding: '12px 32px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 600,
                                border: 'none',
                                backgroundColor: '#0066ff',
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {currentStep === steps.length - 1 ? 'Complete' : 'Continue'} →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingPage;